import clsx from 'clsx'
import { chartFunnelBarGradient } from '@/config/chartTheme'
import type { FunnelPanelData } from '@/types/dashboards'
import FigureValue from './FigureValue'

interface FunnelPanelProps {
  data: FunnelPanelData
  className?: string
}

/**
 * Stage funnel — KYC parties screened through to cleared.
 *
 * Bar width is a share of the largest stage, not of the first: a funnel whose
 * first stage is null still renders honest relative widths for the rest.
 */
export default function FunnelPanel({ data, className }: FunnelPanelProps) {
  const values = data.stages
    .map((stage) => stage.value)
    .filter((value): value is number => value != null)
  const peak = values.length ? Math.max(...values, 1) : 0

  return (
    <div className={clsx('flex min-h-0 flex-col gap-2.5', className)}>
      {data.stages.map((stage, index) => {
        const width = stage.value == null || peak === 0 ? 0 : (stage.value / peak) * 100
        return (
          <div key={stage.key} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-[11px] text-stone-300" title={stage.label}>
                {stage.label}
              </span>
              <span className="flex-shrink-0 font-mono text-[11px] tabular-nums text-stone-100">
                <FigureValue
                  figure={{
                    label: stage.label,
                    value: stage.value,
                    format: 'count',
                    drill: stage.drill,
                  }}
                  align="right"
                />
              </span>
            </div>
            <div className="chart-viz-track h-2 w-full rounded-sm">
              <div
                className="chart-viz-bar chart-viz-bar--rounded-sm h-full"
                style={{
                  width: `${width}%`,
                  background: chartFunnelBarGradient(index, data.stages.length),
                }}
              />
            </div>
            {stage.note && <p className="text-[10px] text-stone-500">{stage.note}</p>}
          </div>
        )
      })}
      {data.footnote && (
        <p className="text-[10px] leading-relaxed text-stone-500">{data.footnote}</p>
      )}
    </div>
  )
}
